import { CoordinatorService } from './coordinator.service';
import { ScriptAttachment } from './script-attachment';

type INP = {
    id?: string;
    targetId: string;
    values?: INPV;
    ownerId?: string;
};

type INPV = {
    title?: string;
    description?: string;
    mimetype?: string;
    url?: string;
    size?: number;
    cachename?: string;
    filename?: string;
    [key: string]: string | number | undefined;
};

export class Attachment {
    id: string;
    targetId: string;
    private _values: INPV;
    private _editValues: INPV | undefined;
    private _ownerId: string;

    private _objectURL?: string;

    constructor(
        public coords: CoordinatorService,
        params: INP,
    ) {
        this.id = params.id || this.coords.uniqueId();
        this._ownerId = params.ownerId || this.coords.user$.currentId;
        this.targetId = params.targetId;
        this._values = Object.assign({}, params.values) || {};
    }

    clone() {
        return new Attachment(this.coords, {
            targetId: '_clone',
            ownerId: this._ownerId,
            values: this._values,
        });
    }

    get isOwned() {
        return this._ownerId === this.coords.user$.currentId;
    }

    get ownerId() {
        return this._ownerId;
    }

    get isDeletable() {
        return this.isOwned;
    }

    get user() {
        return this.coords.user$.get(this._ownerId);
    }

    /**
     * Select a file and store it in IndexedDB
     * Uses the new AttachmentStorage for reliable storage
     * For cloud projects, immediately queues upload to Firebase Storage
     */
    async selectFile(preselectedFile: File | undefined = undefined) {
        const file = preselectedFile || (await this.coords.filesystem$.pickFiles());
        if (!file) return;

        const projectId = this.coords.project$.currentId;
        if (!projectId) {
            console.error('Cannot store attachment: no project selected');
            return;
        }

        const isCloud = this.coords.project$.inCloud;
        const storage = this.coords.attachment$.storage;

        // Store in IndexedDB using the new storage system
        const result = await storage.store(
            file,
            projectId,
            file.name,
            undefined, // Let storage guess mimetype
            isCloud
        );

        if (!result.success || !result.record) {
            console.error('Failed to store attachment:', result.error);
            return;
        }

        const record = result.record;

        // Set edit values from the stored record
        this._editValues = {};
        this._editValues.mimetype = record.mimetype;
        this._editValues.size = record.size;
        this._editValues.filename = record.filename;
        this._editValues.cachename = record.md5Hash; // Use MD5 hash as cachename

        // CRITICAL: For cloud projects, immediately queue upload
        // This ensures the file is uploaded even if user closes browser
        if (isCloud && record.syncStatus === 'pending' && !result.isExisting) {
            console.log('%c Queueing attachment upload', 'color: blue;', { 
                id: record.md5Hash, 
                filename: record.filename 
            });
            this.coords.attachment$.syncManager.queueUpload(record.md5Hash, projectId);
        }

        this.coords.display$.markForCheck();
    }

    get editValues() {
        if (!this._editValues) this._editValues = { ...this._values };
        return this._editValues;
    }

    get title() {
        return this._values.title || this.filename || this.url;
    }

    get url() {
        return this._values.url;
    }

    get mimetype() {
        if (!this._values.mimetype && this.url) {
            try {
                const url = new URL(this.url);
                return this.coords.utils$.guessMimetype(url);
            } catch (e) {}
        }
        return this._values.mimetype || 'application/octet-stream';
    }

    get filename() {
        return this._values.filename;
    }

    get description() {
        return this._values.description;
    }

    /**
     * Flag to prevent multiple simultaneous download attempts
     */
    private _isDownloading = false;

    get imgSrc() {
        if (this._objectURL) return this._objectURL;
        if (this._values.cachename) {
            // Trigger async load without blocking
            this._loadImageAsync();
            return '';
        }
        return this.url;
    }

    /**
     * Async helper for imgSrc to properly handle loading
     */
    private async _loadImageAsync() {
        if (this._isDownloading || this._objectURL) return;
        this._isDownloading = true;
        
        try {
            await this.open();
            this.coords.display$.markForCheck();
        } finally {
            this._isDownloading = false;
        }
    }

    /**
     * Ensure a local copy exists in IndexedDB
     * For cloud projects, downloads from Firebase Storage if not present
     * Call this when displaying attachment widgets to pre-fetch content
     * 
     * @returns true if local copy is available, false otherwise
     */
    async ensureLocalCopy(): Promise<boolean> {
        const cachename = this._values.cachename;
        if (!cachename) return false;

        const projectId = this.coords.project$.currentId;
        if (!projectId) return false;

        const storage = this.coords.attachment$.storage;
        const syncManager = this.coords.attachment$.syncManager;

        // Check if already exists locally
        let record = await storage.get(cachename, projectId);
        if (record) {
            return true;
        }

        // For cloud projects, download on demand
        if (this.coords.project$.current?.isCloud) {
            console.log('%c Downloading attachment for display', 'color: blue;', { 
                cachename, 
                filename: this._values.filename 
            });
            
            record = await syncManager.downloadFromCloud(
                cachename,
                projectId,
                this._values.filename || cachename,
                this._values.mimetype || 'application/octet-stream'
            );
            
            if (record) {
                this.coords.display$.markForCheck();
                return true;
            }
        }

        return false;
    }

    revokeObjectURL() {
        if (!this._objectURL) return;
        URL.revokeObjectURL(this._objectURL);
        this._objectURL = undefined;
        this.coords.display$.markForCheck();
    }

    /**
     * Open the attachment - loads from IndexedDB or downloads from cloud if needed
     * Always serves from local storage, never directly from cloud
     */
    async open(): Promise<any> {
        const cache = this._values.cachename;

        if (cache) {
            if (!this._objectURL) {
                try {
                    const projectId = this.coords.project$.currentId;
                    if (!projectId) return { error: 'No project selected' };

                    const storage = this.coords.attachment$.storage;
                    const syncManager = this.coords.attachment$.syncManager;

                    // Try to get from IndexedDB first
                    let record = await storage.get(cache, projectId);

                    // If not found locally and this is a cloud project, download on demand
                    if (!record && this.coords.project$.current?.isCloud) {
                        console.log('Attachment not found locally, downloading from cloud...', { cache });
                        record = await syncManager.downloadFromCloud(
                            cache,
                            projectId,
                            this._values.filename || cache,
                            this._values.mimetype || 'application/octet-stream'
                        );
                    }

                    if (!record) {
                        return { error: 'CACHE not found' };
                    }

                    // Create object URL from the blob
                    this._objectURL = URL.createObjectURL(
                        new Blob([record.blob], {
                            type: this._values.mimetype || record.mimetype,
                        }),
                    );

                    // Auto-revoke after 200 minutes (same as before)
                    const revokeDelay = 12000000;
                    // Using a closure to capture the reference
                    const self = this;
                    const revokeTimer = () => {
                        self.revokeObjectURL();
                    };
                    globalThis.setTimeout(revokeTimer, revokeDelay);
                } catch (e) {
                    console.error('Failed to open attachment:', e);
                    return { error: 'Failed to open attachment' };
                }
            }
            return { objectURL: this._objectURL };
        }

        return { error: 'No cachename' };
    }

    get isImage() {
        return this.mimetype?.startsWith('image/');
    }

    get isPDF() {
        return this.mimetype?.startsWith('application/pdf');
    }

    /**
     * Unload attachment from memory only (used during reload/load3)
     * Does NOT delete from IndexedDB or trigger manifest store
     * This is critical for avoiding data loss during project reload
     */
    unload() {
        this.revokeObjectURL();
        this.coords.attachment$.delete(this);
    }

    /**
     * Delete the attachment - removes from IndexedDB and metadata
     * Use this ONLY for user-initiated deletions, NOT during reload
     */
    async delete() {
        this.revokeObjectURL();
        
        // Delete from IndexedDB storage
        const cachename = this._values.cachename;
        const projectId = this.coords.project$.currentId;
        if (cachename && projectId) {
            await this.coords.attachment$.storage.delete(cachename, projectId);
        }
        
        this.coords.attachment$.delete(this);
        this.coords.display$.markForCheck();
        this.coords.attachment$.markForManifestStore();
    }

    get deleteParams() {
        return {
            icon: 'delete',
            fun: () => this.delete(),
        };
    }

    cancelEdit() {
        this._editValues = undefined;
        this.coords.display$.closeDialog();
    }

    resetFileValues() {
        // reset all fields in editValues except description
        const description = this._editValues?.description;
        this._editValues = { description };
    }

    async informFileSaved(fH: FileSystemFileHandle) {
        // Legacy method - no longer needed with IndexedDB storage
    }

    get isSubmitDisabled() {
        return !this._editValues!.url && !this._editValues!.cachename;
    }

    private get _remotePath() {
        const cachename = this._values.cachename;
        const pId = this.coords.project$.currentId;
        const uId = this.coords.project$.current?.team?.owner.id;
        return `userdata/${uId}/projects/${pId}/Attachments/${cachename}`;
    }

    /**
     * Upload attachment to cloud storage
     * Now handled by the sync manager with exponential backoff retry
     */
    async cloudUpload(): Promise<boolean> {
        const cachename = this._values.cachename;
        if (!cachename) {
            console.error('No attachment file to upload');
            return false;
        }

        const projectId = this.coords.project$.currentId;
        if (!projectId) {
            console.error('No project selected');
            return false;
        }

        try {
            // Queue the upload with the sync manager
            await this.coords.attachment$.syncManager.queueUpload(cachename, projectId);
            return true;
        } catch (e: any) {
            console.error('Failed to queue cloud upload:', e);
            return false;
        }
    }

    /**
     * Submit the attachment - saves metadata and triggers cloud upload if needed
     */
    async submit(params?: any) {
        if (params) this._editValues = params;
        this._values = { ...this._editValues };
        this._editValues = undefined;
        
        const cachename = this._values.cachename;
        
        // For cloud projects, the upload is already queued when the file was selected
        // Just verify it's in progress or completed
        if (cachename && this.coords.project$.inCloud) {
            const projectId = this.coords.project$.currentId;
            if (projectId) {
                const storage = this.coords.attachment$.storage;
                const record = await storage.get(cachename, projectId);
                
                if (record && record.syncStatus === 'failed') {
                    // Retry failed upload
                    await this.cloudUpload();
                }
            }
        }

        console.log('Attachment submitted:', this._values);
        this.coords.attachment$.add(this);
        this.coords.display$.markForCheck();
        this.coords.attachment$.markForManifestStore();
    }

    save() {
        return {
            id: this.id,
            targetId: this.targetId,
            values: this._values,
        };
    }

    assign(targetId: string) {
        this.targetId = targetId;
        this.coords.attachment$.add(this);
    }

    get scriptClass() {
        return new ScriptAttachment(this.coords, this);
    }

    /**
     * Get sync status for this attachment (for cloud projects)
     */
    async getSyncStatus(): Promise<'pending' | 'uploading' | 'synced' | 'failed' | 'local' | null> {
        const cachename = this._values.cachename;
        if (!cachename) return null;
        
        const projectId = this.coords.project$.currentId;
        if (!projectId) return null;

        if (!this.coords.project$.inCloud) {
            return 'local'; // Local project - no sync needed
        }

        const record = await this.coords.attachment$.storage.get(cachename, projectId);
        return record?.syncStatus || null;
    }
}
